import { Component } from 'react';
import type { ReactNode } from 'react';

import { LoggerUtil } from '../../../utils/logger-util';
import { ApiError } from '../../../classes/api-error';
import { CookieUtil } from '../../../utils/cookie-util';
import { DesignSysEnvUtil } from '../../../utils/design-sys-env-util';

export type ErrorBoundaryProps = {
  children: ReactNode;
  errorLayout: (error: Error | ApiError | null) => ReactNode;
  ignoreUnauthorizedError?: boolean;
};

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  { hasError: Error | ApiError | null }
> {
  static getDerivedStateFromError(error: Error | ApiError) {
    return { hasError: error };
  }

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: null };
  }

  override componentDidCatch(error: Error | ApiError) {
    const { ignoreUnauthorizedError } = this.props;
    if (
      !ignoreUnauthorizedError &&
      error instanceof ApiError &&
      error.getStatusCode() === 401
    ) {
      CookieUtil.clearUserAndReload(DesignSysEnvUtil.cookieDomain);
    } else {
      LoggerUtil.fireError(error);
      this.setState({ hasError: error });
    }
  }

  override render() {
    const { hasError } = this.state;
    const { children, errorLayout } = this.props;
    return hasError ? errorLayout(hasError) : children;
  }
}
